import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../../../../../redux/store";
import { useTorRules } from "../../../../BookingForm/hooks/useTorRules";
import type { CommercialRenewalStepProps } from "../types";
import { useHydratePaymentPlans } from "./useHydratePaymentPlans";
import { usePaymentPlansMutations } from "./usePaymentPlansMutations";
import { usePaymentPlansStaleRegistry } from "./usePaymentPlansStaleRegistry";
import { usePaymentPlansYearLimit } from "./usePaymentPlansYearLimit";
import { useSavePaymentPlans } from "./useSavePaymentPlans";

type UsePaymentPlansStepControllerProps = Pick<
  CommercialRenewalStepProps,
  "paymentPlansInvalidated" | "onPaymentPlansSaved"
>;

export const usePaymentPlansStepController = (props: UsePaymentPlansStepControllerProps) => {
  const { paymentPlansInvalidated = false, onPaymentPlansSaved } = props;
  const dispatch = useDispatch<AppDispatch>();

  const { isTurnoverRentOnly } = useTorRules();
  const { selectedBreakdownMode, yearLimit, isSeparateInvoicingEnabled, isleaseTypeTor, leaseId } =
    usePaymentPlansYearLimit();

  // Field array before hydrate so replace can update fields directly.
  const { plansFields, plansReplace, handleRemovePlan, handleAddPlan, paymentPlanResetKey, canAddPlan } =
    usePaymentPlansMutations({ yearLimit });

  useHydratePaymentPlans({ paymentPlansInvalidated, replace: plansReplace });

  const { registerPaymentPlanBreakdownStale, registerServiceChargeBreakdownStale, assertNoStaleCommercialBreakdowns } =
    usePaymentPlansStaleRegistry({ isSeparateInvoicingEnabled, yearLimit, isTurnoverRentOnly, isleaseTypeTor });

  const { savePaymentPlans, isPendingSavePaymentPlans } = useSavePaymentPlans({
    dispatch,
    leaseId,
    onSuccess: onPaymentPlansSaved,
  });

  return {
    plansFields,
    selectedBreakdownMode,
    paymentPlanResetKey,
    canAddPlan,
    yearLimit,
    handleAddPlan,
    handleRemovePlan,
    registerPaymentPlanBreakdownStale,
    registerServiceChargeBreakdownStale,
    assertNoStaleCommercialBreakdowns,
    savePaymentPlans,
    isPendingSavePaymentPlans,
  };
};
